import React from 'react';
import { t } from 'i18next';

import type { SelectOption } from '@app/Utilities/SelectMap';
import type { IndUpdateDTO } from '@api/DTO/Ind/Ind/dto';

import { PersonalDetailsForm } from './PersonalDetailsForm';
import type { PersonalDetailsInitialValues } from './types';

import { useRepository } from '@app/Hooks/useRepository';
import { IndRepository, UserSourcesRepository } from '@api/index';
import type { Ind } from '@api/Entities/Ind/Ind';
import { getIndGenderSelectOptions } from '@api/Entities/Ind/Ind';

import { EditableFormCard } from '@app/Components/Forms/EditableFormCard';
import { useModalStack } from '@app/UI/Modals/ModalStackProvider';

export type PersonalDetailsFormDelegatorProps = {
  userId: number;
  profileId: number;
  initialValues?: Partial<PersonalDetailsInitialValues>;
  onSave?: (ind: Ind) => Promise<void>;
};

export const PersonalDetailsFormDelegator = ({
  userId,
  profileId,
  initialValues,
  onSave,
}: PersonalDetailsFormDelegatorProps) => {
  const userSourceRepository = useRepository(UserSourcesRepository);
  const indRepository = useRepository(IndRepository);

  const [isSaving, setIsSaving] = React.useState<boolean>(false);
  const [updatedIndSource, setUpdatedIndSource] = React.useState<number | null>(null);
  const [userSources, setUserSources] = React.useState<Array<SelectOption<number>>>([]);
  const [userSourcesLoading, setUserSourcesLoading] = React.useState<boolean>(true);

  const { open } = useModalStack();

  const genderOptions = React.useMemo(() => getIndGenderSelectOptions(t), [t]);

  const refreshUserSources = React.useCallback(async () => {
    setUserSourcesLoading(true);
    try {
      const col = await userSourceRepository.getUserSources(userId, {
        limit: 100,
      });

      setUserSources(
        col.getItems().map((s) => ({
          value: s.getSourceId(),
          label: s.getSourceName(),
        }))
      );
    } finally {
      setUserSourcesLoading(false);
    }
  }, [userSourceRepository, userId]);

  React.useEffect(() => {
    void refreshUserSources();
  }, [refreshUserSources]);

  const handleSubmit = React.useCallback(
    async (values: IndUpdateDTO) => {
      setIsSaving(true);
      try {
        const updated = await indRepository.updateInd(profileId, values);
        await onSave?.(updated);
      } finally {
        setIsSaving(false);
      }
    },
    [indRepository, profileId, onSave]
  );

  const handleAddUserSource = React.useCallback(async () => {
    try {
      const createdUserSource = await open('userSourceCreate', { userId });

      setUserSources((prev) => [
        ...prev,
        {
          value: createdUserSource.getSourceId(),
          label: createdUserSource.getSourceName(),
        },
      ]);

      setUpdatedIndSource(createdUserSource.getSourceId());
    } catch {
      return;
    }
  }, [open, userId]);

  return (
    <EditableFormCard title={t('agent.profile.personalDetailsForm.title')}>
      {({ editing }) => (
        <PersonalDetailsForm
          initialValues={initialValues}
          genderOptions={genderOptions}
          updatedIndSource={updatedIndSource}
          sourceOptions={userSources}
          loadingSourceOptions={userSourcesLoading}
          onSubmit={handleSubmit}
          onAddUserSource={handleAddUserSource}
          mode={editing ? 'edit' : undefined}
          isSaving={isSaving}
        />
      )}
    </EditableFormCard>
  );
};
